// O REPASSE POR CREDOR FINAL (#280), na tela da consignataria.
//
// A ASSALCE recebe da ALECE um repasse so' e o reparte: o da 641 e' da Unimed,
// o da 640 da Uniodonto, o das rubricas proprias fica com ela. Aqui o resultado
// do arquivo confirmado aparece agrupado pelo credor final de cada rubrica, com
// o repassado rateado pelo descontado de cada grupo.

import { useQuery } from "@tanstack/react-query";
import { Button, Card, DataTable, EstadoVazio, fmtBRL, type Column } from "@atlas/ui/web";
import type { GrupoCredorFinal } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";

const reais = (c: number) => fmtBRL(c / 100);

export function RepassePorCredorFinal({
  credorId,
  unidadeId,
  competencia,
  salvar,
}: {
  credorId: number;
  unidadeId: number;
  competencia: string;
  salvar: (blob: Blob, nome: string) => void;
}) {
  // Sob a chave do resultado: registrar um repasse invalida os dois juntos.
  const q = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "resultado", "credor-final", unidadeId, competencia],
    queryFn: () => atlas.admin.consignatarias.repassePorCredorFinal(credorId, { unidadeId, competencia }),
    retry: false,
  });
  const grupos = q.data?.grupos ?? [];
  const total = (k: "linhas" | "descontadoCentavos" | "repassadoCentavos" | "saldoCentavos") =>
    grupos.reduce((t, g) => t + g[k], 0);
  const colunas: Column<GrupoCredorFinal>[] = [
    { key: "credor", header: "Credor final", render: (g) => g.credorFinalNome },
    { key: "natureza", header: "Natureza", render: (g) => g.naturezaJuridica?.replace(/_/g, " ") ?? "—" },
    { key: "rubricas", header: "Rubricas", mono: true, render: (g) => g.rubricas.join(", ") || "—" },
    { key: "linhas", header: "Linhas", align: "right", render: (g) => g.linhas },
    { key: "descontado", header: "Descontado", align: "right", render: (g) => reais(g.descontadoCentavos) },
    { key: "repassado", header: "Repassado", align: "right", render: (g) => reais(g.repassadoCentavos) },
    { key: "saldo", header: "Saldo", align: "right", render: (g) => reais(g.saldoCentavos) },
  ];
  const exportar = async () => {
    const blob = await atlas.admin.consignatarias.repassePorCredorFinalCsv(credorId, { unidadeId, competencia });
    salvar(blob, `repasse-credor-final-${competencia}.csv`);
  };
  return (
    <Card>
      <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Repasse por credor final — {competencia}</h2>
        {grupos.length > 0 ? <Button variant="ghost" onClick={() => void exportar()}>Exportar CSV</Button> : null}
      </div>
      <p style={{ margin: "4px 0 12px", color: "var(--text-dim)" }}>
        O repasse do órgão é um só; ele é rateado entre os credores finais pelo que a folha descontou de cada um.
        Rubrica sem credor final fica com a própria consignatária.
      </p>
      {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível montar o repasse por credor final.")}</p>}
      <DataTable
        caption="Repasse por credor final"
        columns={colunas}
        rows={grupos}
        rowKey={(g) => String(g.credorFinalId)}
        loading={q.isLoading}
        emptyState={<EstadoVazio titulo="Nenhuma linha aceita neste arquivo." />}
      />
      {grupos.length > 1 ? (
        <p style={{ margin: "8px 0 0", fontWeight: 600 }}>
          Total: {total("linhas")} linha(s) · descontado {reais(total("descontadoCentavos"))} · repassado{" "}
          {reais(total("repassadoCentavos"))} · saldo {reais(total("saldoCentavos"))}
        </p>
      ) : null}
    </Card>
  );
}
